/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';
import { BasicAlert, type BasicAlertParams } from '@g17eco/molecules/alert';
import { DataTransferMappingStatus, IntegrationValueSource, SurveyDataStatus, ValidationNoticeType, type DataTransferConnectionInfo, type DataTransferRow, type ValidationNotice } from '@g17eco/types/data-transfer';

export const getRowKey = (row: DataTransferRow): string => {
  let key = row.utrCode;
  if (row.optionCode) {
    key += `:${row.optionCode}`;
  }
  if (row.tableRowMatch && row.tableRowMatch.length > 0) {
    key += `[${row.tableRowMatch.map((m) => `${m.code}=${m.value}`).join(',')}]`;
  }
  return key;
};

export interface Filters {
  connection: '' | 'connected' | 'missing';
  survey: '' | 'value' | 'missing';
  status: '' | DataTransferMappingStatus | 'verified';
  validation: '' | 'errors' | 'warnings' | 'notes' | 'partner_notes' | 'discrepancy' | 'outside_tolerance' | 'both_differ' | 'within_tolerance' | 'excluded' | 'clean';
}

export const applyFilters = (rows: DataTransferRow[], filters: Filters, search: string, excludedRows?: Set<string>): DataTransferRow[] => {
  const searchLower = search.toLowerCase();
  return rows.filter((row) => {
    if (searchLower && !row.utrCode.toLowerCase().includes(searchLower) && !row.utrLabel.toLowerCase().includes(searchLower) && !(row.optionLabel ?? '').toLowerCase().includes(searchLower)) {
      return false;
    }

    if (filters.connection === 'connected' && !row.connectionInfo) {
      return false;
    }
    if (filters.connection === 'missing' && row.connectionInfo) {
      return false;
    }

    if (filters.survey === SurveyDataStatus.Value && row.survey.status !== SurveyDataStatus.Value) {
      return false;
    }
    if (filters.survey === 'missing' && row.survey.status === SurveyDataStatus.Value) {
      return false;
    }

    if (filters.status) {
      if (filters.status === 'verified') {
        if (!row.verifiedAt) {
          return false;
        }
      } else if (row.status !== filters.status) {
        return false;
      }
    }

    if (filters.validation === 'errors' && !row.notices.some((n) => n.type === ValidationNoticeType.Error)) {
      return false;
    }
    if (filters.validation === 'warnings' && !row.notices.some((n) => n.type === ValidationNoticeType.Warning)) {
      return false;
    }
    if (filters.validation === 'notes' && !row.internalNotes) {
      return false;
    }
    if (filters.validation === 'partner_notes' && !row.partnerNotes) {
      return false;
    }
    if (filters.validation === 'discrepancy' && !row.hasDiscrepancy && !row.toleranceResult) {
      return false;
    }
    if (filters.validation === 'outside_tolerance' && (!row.hasDiscrepancy || row.toleranceResult)) {
      return false;
    }
    // Shows rows where both sides actually have a value AND the
    // discrepancy is outside tolerance — hides rows where survey is
    // Not Answered / missing so the reviewer only sees real mismatches
    // between two populated values.
    if (filters.validation === 'both_differ' && (
      row.survey.value === undefined
      || row.integration.value === undefined
      || !row.hasDiscrepancy
      || row.toleranceResult
    )) {
      return false;
    }
    if (filters.validation === 'within_tolerance' && !row.toleranceResult) {
      return false;
    }
    if (filters.validation === 'excluded') {
      return excludedRows ? excludedRows.has(getRowKey(row)) : false;
    }
    if (excludedRows && excludedRows.has(getRowKey(row))) {
      return false;
    }
    if (filters.validation === 'clean' && (row.notices.length > 0 || row.internalNotes || row.partnerNotes)) {
      return false;
    }

    return true;
  });
};

export const getStatusBadgeClass = (status: DataTransferMappingStatus): string => {
  switch (status) {
    case DataTransferMappingStatus.Active:
      return 'background-ThemeSuccessMedium text-ThemeTextWhite';
    case DataTransferMappingStatus.Pending:
      return 'background-ThemeIconSecondary text-ThemeTextWhite';
    case DataTransferMappingStatus.NoData:
      return 'background-ThemeWarningMedium text-ThemeTextWhite';
  }
};

export const getStatusLabel = (status: DataTransferMappingStatus): string => {
  switch (status) {
    case DataTransferMappingStatus.Active:
      return 'active';
    case DataTransferMappingStatus.Pending:
      return 'pending';
    case DataTransferMappingStatus.NoData:
      return 'no data';
  }
};

export const getSurveyDataStatusLabel = (status: SurveyDataStatus): string => {
  switch (status) {
    case SurveyDataStatus.Value:
      return 'Value';
    case SurveyDataStatus.Na:
      return 'N/A';
    case SurveyDataStatus.NoSurvey:
      return 'No Survey';
    case SurveyDataStatus.NotAnswered:
      return 'Not Answered';
  }
};

export const getSurveyDataStatusClass = (status: SurveyDataStatus): string => {
  switch (status) {
    case SurveyDataStatus.Value:
      return 'text-ThemeSuccessMedium';
    case SurveyDataStatus.Na:
      return 'text-ThemeTextLight';
    case SurveyDataStatus.NoSurvey:
      return 'text-ThemeDangerMedium';
    case SurveyDataStatus.NotAnswered:
      return 'text-ThemeWarningMedium';
  }
};

export const renderUnitInfo = (unit?: string, numberScale?: string) => {
  if (!unit && !numberScale) {
    return null;
  }
  const parts = [numberScale, unit].filter(Boolean).join(' ');
  return <span className='small text-ThemeTextLight ms-2'>({parts})</span>;
};

const NOTICE_ALERT_TYPE: Record<ValidationNoticeType, BasicAlertParams['type']> = {
  [ValidationNoticeType.Info]: 'info',
  [ValidationNoticeType.Warning]: 'warning',
  [ValidationNoticeType.Error]: 'danger',
};

export const renderNoticeAlerts = (notices: ValidationNotice[], className?: string) =>
  notices.map((notice) => (
    <BasicAlert key={notice.code} type={NOTICE_ALERT_TYPE[notice.type]} className={className}>
      {notice.message}
    </BasicAlert>
  ));

export const getSourceLabel = (source?: IntegrationValueSource): string | null => {
  if (!source) {
    return null;
  }
  switch (source) {
    case IntegrationValueSource.Connection:
      return 'Conn';
    case IntegrationValueSource.ApiPassthrough:
      return 'API';
    case IntegrationValueSource.StaticValue:
      return 'Static';
    case IntegrationValueSource.ClientOverride:
      return 'Override';
    case IntegrationValueSource.SubsidiaryOverride:
      return 'Subsidiary Override';
    case IntegrationValueSource.Derived:
      return 'Derived';
  }
};

export const getSurveyUrl = (params: { surveyId?: string; utrvId?: string }, childInitiativeId: string): string | undefined => {
  if (!params.surveyId) {
    return undefined;
  }
  if (params.utrvId) {
    const baseUrl = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
      initiativeId: childInitiativeId,
      surveyId: params.surveyId,
      page: 'question',
    });
    return `${baseUrl}/${params.utrvId}`;
  }
  return generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
    initiativeId: childInitiativeId,
    surveyId: params.surveyId,
    page: 'overview',
  });
};

export const getSourceInfo = (source?: IntegrationValueSource, connectionInfo?: DataTransferConnectionInfo, detail?: string): { icon: string; color: string; label: string; tooltip: string } | null => {
  if (!source) {
    return null;
  }
  switch (source) {
    case IntegrationValueSource.Connection: {
      const connectionDetail = connectionInfo
        ? `${connectionInfo.calculationName} · ${connectionInfo.connectionCode} · Variable: ${connectionInfo.variableCode} · Value List: ${connectionInfo.valueListCode}`
        : 'Static calculation connection';
      return { icon: 'fa-link', color: 'text-ThemeSuccessMedium', label: 'Connection', tooltip: connectionDetail };
    }
    case IntegrationValueSource.ApiPassthrough:
      return { icon: 'fa-cloud-download', color: 'text-ThemeInfoMedium', label: 'API', tooltip: 'Direct value from integration API raw data' };
    case IntegrationValueSource.StaticValue:
      return { icon: 'fa-file-text-o', color: 'text-ThemeIconSecondary', label: 'Static', tooltip: 'Static methodology value (same for all clients)' };
    case IntegrationValueSource.ClientOverride:
      return { icon: 'fa-globe', color: 'text-ThemeWarningMedium', label: 'Override', tooltip: 'Client-wide override — applied to every subsidiary under this client' };
    case IntegrationValueSource.SubsidiaryOverride:
      return { icon: 'fa-building', color: 'text-ThemeWarningMedium', label: 'Subsidiary Override', tooltip: 'Subsidiary-specific override — only applied to this subsidiary' };
    case IntegrationValueSource.Derived:
      return { icon: 'fa-calculator', color: 'text-ThemeInfoMedium', label: 'Derived', tooltip: detail ?? 'Derived from API data for a different date range' };
  }
};
